import { ApiError } from '../http/api-error.ts';
import type { ApiClient } from '../http/client.ts';
import { SseParser, type SseMessage } from './sse-parser.ts';

/**
 * - `connecting`: first connection.
 * - `live`: the stream is open and the server said `ready`.
 * - `reconnecting`: it was live (or tried) and is coming back.
 * - `offline`: the browser has no internet, or the server refused the session.
 * - `stopped`: closed on purpose.
 */
export type LiveStatus = 'connecting' | 'live' | 'reconnecting' | 'offline' | 'stopped';

/** The `change` payload: small invalidations; the screens refetch what the event names. */
export type LiveChange = { type: string; topics: string[]; id?: string; data?: Record<string, unknown> };

export type LiveConnectionOptions = {
  client: Pick<ApiClient, 'openStream'>;
  path: string;
  onChange?: (event: LiveChange) => void;
  /** The server lost track of what this client saw: refetch everything. */
  onResync?: () => void;
  /** `reconnected` is true after a drop: events may have been missed, so refetch everything. */
  onReady?: (info: { reconnected: boolean }) => void;
  onStatus?: (status: LiveStatus) => void;
  /** The session is no longer valid for this stream (401/403); the connection stops. */
  onFatal?: (error: ApiError) => void;
  initialDelayMs?: number;
  maxDelayMs?: number;
  /** The server sends a keepalive every 25 s; a silent stream for longer than this is reopened. */
  idleTimeoutMs?: number;
  random?: () => number;
};

const DEFAULTS = { initialDelayMs: 1000, maxDelayMs: 30_000, idleTimeoutMs: 60_000 };

/** Growing wait with jitter: 1 s, 2 s, 4 s… up to the maximum, each between half and the full value. */
export function backoffDelay(attempt: number, initialMs: number, maxMs: number, random: () => number = Math.random): number {
  const base = Math.min(maxMs, initialMs * 2 ** Math.max(0, attempt - 1));
  return Math.round(base * (0.5 + random() * 0.5));
}

const isFatal = (error: unknown): error is ApiError => error instanceof ApiError && (error.status === 401 || error.status === 403);

function parseChange(message: SseMessage): LiveChange | null {
  try {
    const value = JSON.parse(message.data) as Partial<LiveChange> & { topic?: string };
    if (!value || typeof value.type !== 'string') return null;
    const topics = Array.isArray(value.topics) ? value.topics : typeof value.topic === 'string' ? [value.topic] : [];
    return { type: value.type, topics, id: value.id, data: value.data };
  } catch {
    return null;
  }
}

/**
 * One live connection to `/v1/<surface>/events`, independent of React. Reads the stream with fetch
 * (EventSource cannot send the Authorization header) and reconnects with a growing wait.
 */
export class LiveConnection {
  private status: LiveStatus = 'stopped';
  private running = false;
  private controller: AbortController | null = null;
  private wake: (() => void) | null = null;
  private watchdog: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;
  private everReady = false;
  private serverRetryMs: number | null = null;
  private readonly options: LiveConnectionOptions & typeof DEFAULTS;

  constructor(options: LiveConnectionOptions) {
    // An option passed as undefined keeps its default (React hooks forward their optional tuning).
    const given = Object.fromEntries(Object.entries(options).filter(([, value]) => value !== undefined)) as LiveConnectionOptions;
    this.options = { ...DEFAULTS, ...given };
  }

  get currentStatus(): LiveStatus {
    return this.status;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }
    void this.loop();
  }

  stop(): void {
    if (!this.running) return;
    this.detach();
    this.controller?.abort();
    this.wake?.();
    this.setStatus('stopped');
  }

  private detach(): void {
    this.running = false;
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
    }
  }

  private readonly handleOnline = () => {
    this.attempt = 0;
    this.wake?.();
  };

  private readonly handleOffline = () => {
    this.controller?.abort();
  };

  private setStatus(status: LiveStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.options.onStatus?.(status);
  }

  private sleep(ms: number | null): Promise<void> {
    return new Promise(resolve => {
      const timer = ms === null ? null : setTimeout(finish, ms);
      function finish() {
        if (timer) clearTimeout(timer);
        resolve();
      }
      this.wake = () => {
        this.wake = null;
        finish();
      };
    });
  }

  private async loop(): Promise<void> {
    while (this.running) {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        this.setStatus('offline');
        await this.sleep(null);
        continue;
      }
      this.setStatus(this.everReady ? 'reconnecting' : this.attempt === 0 ? 'connecting' : 'reconnecting');
      let gotReady = false;
      try {
        gotReady = await this.connectOnce();
      } catch (error) {
        if (isFatal(error)) {
          this.detach();
          this.setStatus('offline');
          this.options.onFatal?.(error);
          return;
        }
      }
      if (!this.running) return;
      this.attempt = gotReady ? 1 : this.attempt + 1;
      this.setStatus('reconnecting');
      const initial = this.serverRetryMs ?? this.options.initialDelayMs;
      await this.sleep(backoffDelay(this.attempt, Math.min(initial, this.options.maxDelayMs), this.options.maxDelayMs, this.options.random));
    }
  }

  private armWatchdog(controller: AbortController): void {
    if (this.watchdog) clearTimeout(this.watchdog);
    this.watchdog = setTimeout(() => controller.abort(), this.options.idleTimeoutMs);
  }

  private clearWatchdog(): void {
    if (this.watchdog) clearTimeout(this.watchdog);
    this.watchdog = null;
  }

  /** Resolves when the stream ends; true if the server said `ready` during it. Throws fatal errors. */
  private async connectOnce(): Promise<boolean> {
    const controller = new AbortController();
    this.controller = controller;
    let ready = false;
    const parser = new SseParser({
      onMessage: message => {
        if (!this.running || controller.signal.aborted) return;
        if (message.event === 'ready') {
          ready = true;
          this.attempt = 0;
          this.setStatus('live');
          const reconnected = this.everReady;
          this.everReady = true;
          this.options.onReady?.({ reconnected });
        } else if (message.event === 'change') {
          const change = parseChange(message);
          if (change) this.options.onChange?.(change);
        } else if (message.event === 'resync') {
          this.options.onResync?.();
        }
      },
      onRetry: ms => {
        this.serverRetryMs = ms;
      },
      onActivity: () => this.armWatchdog(controller),
    });
    try {
      const response = await this.options.client.openStream(this.options.path, controller.signal);
      if (!this.running || controller.signal.aborted) {
        await response.body?.cancel();
        return false;
      }
      this.armWatchdog(controller);
      const reader = response.body!.getReader();
      // Aborting ends the read even if the body is not tied to the fetch signal.
      controller.signal.addEventListener('abort', () => void reader.cancel().catch(() => {}), { once: true });
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        parser.push(decoder.decode(value, { stream: true }));
      }
      return ready;
    } catch (error) {
      if (isFatal(error)) throw error;
      return ready;
    } finally {
      this.clearWatchdog();
      controller.abort();
      if (this.controller === controller) this.controller = null;
    }
  }
}

export const LIVE_STATUS_LABEL: Record<LiveStatus, string> = {
  connecting: 'Conectando…',
  live: 'En vivo',
  reconnecting: 'Reconectando…',
  offline: 'Sin conexión',
  stopped: 'Desconectado',
};
